import React from 'react';
import { StarIcon } from 'lucide-react';
import { ResolvedRequirement, Role } from '../types/commission';
import { formatCurrency } from '../utils/commission';
import { RequirementRow } from './RequirementRow';
interface RoleSummaryCardProps {
  role: Role;
  units: number;
  activeStepIndex: number;
  requirements: ResolvedRequirement[];
}

/** Current-role card for leadership and operations plans. */
export function RoleSummaryCard({
  role,
  units,
  activeStepIndex,
  requirements
}: RoleSummaryCardProps) {
  const topStep = role.steps[role.steps.length - 1];
  const structureLabel = role.model === 'bps' ? `${role.baseBps} BPS base · up to ${topStep.totalBps} BPS` : `Up to ${formatCurrency(topStep.perUnit ?? 0)} per ${role.incentiveNoun ?? 'unit'}`;
  return <article aria-labelledby="role-summary-heading" className="flex h-full flex-col overflow-hidden rounded-xl border border-brand-900 bg-brand-900 p-6 shadow-card" data-id="element-478" data-mp="y4qkzhuqhk2f">
      <div className="flex items-start justify-between gap-4" data-id="element-479" data-mp="2yvsm7lz0tn6a">
        <div data-id="element-480" data-mp="2yv9cutnfs73v">
          <p className="text-xs font-semibold uppercase tracking-widest text-brand-200" data-id="element-481" data-mp="2ytol1bsib29p">Current role</p>
          <h2 id="role-summary-heading" className="mt-1.5 text-2xl font-bold text-white" data-id="element-482" data-mp="33ssv04n99413">
            {role.name}
          </h2>
        </div>
        <span aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/25" data-id="element-483" data-mp="3dkyip26h4zah">
          <StarIcon className="h-5 w-5 fill-gold-400 text-gold-400" data-id="element-484" data-mp="2kw00y669jz5p" />
        </span>
      </div>

      <div className="mt-4 flex flex-wrap gap-2" data-id="element-485" data-mp="2pv468lq2hcqr">
        <span className="rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold text-brand-100" data-id="element-486" data-mp="2pub9mdkto3mc">
          {role.scope} production
        </span>
        <span className="rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold text-brand-100" data-id="element-487" data-mp="54vljqczmt0e">
          {structureLabel}
        </span>
      </div>

      <div className="mt-5" data-id="element-488" data-mp="94vyt7ocoy5v">
        <div className="flex gap-1.5" aria-hidden="true" data-id="element-489" data-mp="98bgmi9eslaz">
          {role.steps.map((step, index) => <span key={step.minUnits} className={`h-1.5 flex-1 rounded-full ${index <= activeStepIndex ? 'bg-gold-400' : 'bg-white/20'}`} data-id="element-490" data-mp="2180cl4w6r79w" data-mpi={index} />)}
        </div>
        <p className="mt-2 text-xs font-medium text-brand-200" data-id="element-491" data-mp="2a8op1v5xkzko">
          {units} {role.unitNounPlural}
        </p>
      </div>

      {role.monthlyCap ? <p className="mt-4 rounded-lg bg-white/10 px-3 py-2 text-xs font-semibold text-brand-100" data-id="element-492" data-mp="1mjncbvq2f29l">
          Monthly commission capped at {formatCurrency(role.monthlyCap)}
        </p> : null}

      {role.promotion && requirements.length > 0 ? <div className="mt-auto space-y-4 pt-6" data-id="element-493" data-mp="1rigpfn1fwr98">
          <p className="text-sm font-semibold text-white" data-id="element-494" data-mp="1rgvy06af9ofl">
            Next role progress
            <span className="ml-2 text-xs font-medium uppercase tracking-wide text-brand-300" data-id="element-495" data-mp="13r1ljpu0m1vv">
              {role.promotion.toRoleName}
            </span>
          </p>
          {requirements.map(requirement => <RequirementRow key={requirement.label} requirement={requirement} data-id="element-496" data-mp="3aes77o1w697d" />)}
        </div> : <p className="mt-auto pt-6 text-sm font-medium text-brand-200" data-id="element-497" data-mp="3f9fvblszh1lf">
          Paid on {role.scope.toLowerCase()} production — no further role above this plan.
        </p>}
    </article>;
}